/**
 * Beräknar batteribesparingen per månad i kronor.
 *
 * Används av både MonthlySavingsChart (för stapeldiagram) och
 * WinterInsightCard (för vinter-vs-årssumma). Att de delar samma
 * funktion garanterar att siffrorna matchar — det går inte att en
 * komponent säger "+50 kr på vintern" medan den andra säger "förlust".
 *
 * Formel per timme:
 *   baselineCost = importKWh × importPrice - exportKWh × exportRevenue
 *   batteryCost  = importKWh × importPrice - exportKWh × exportRevenue
 *                  (men med batteri-data och batteri-årsförbrukning för tier)
 *   saving       = baselineCost - batteryCost
 *
 * Summa per kalendermånad (i hours data; om datat spänner över årsgräns
 * blir nycklar som "2025-12" och "2026-01" separata).
 */

import type { HourPoint, ResultHour, Scenario } from "./types";
import { importPricePerKWh, exportRevenuePerKWh } from "./costs";

export interface MonthlySavings {
  /** "YYYY-MM" sorterbart format */
  monthKey: string;
  /** Månadens nummer (0-11) — bra för logik som "är detta vinter?" */
  monthIndex: number;
  /** År */
  year: number;
  /** Besparing i kronor (kan vara negativ) */
  savingsSek: number;
}

export function computeMonthlySavings(
  baselineHours: HourPoint[],
  resultHours: ResultHour[],
  scenario: Scenario,
): MonthlySavings[] {
  const baselineAnnualImport = baselineHours.reduce(
    (s, h) => s + h.importKWh,
    0,
  );
  const batteryAnnualImport = resultHours.reduce(
    (s, h) => s + h.importKWh,
    0,
  );

  const byMonth = new Map<string, { monthIndex: number; year: number; saving: number }>();

  // Använder index-iteration eftersom baselineHours och resultHours är
  // parallella arrayer från simulate(). Säkrare än timestamp-lookup.
  const len = Math.min(baselineHours.length, resultHours.length);
  for (let i = 0; i < len; i++) {
    const bh = baselineHours[i];
    const rh = resultHours[i];
    if (!bh || !rh) continue;

    const date = new Date(bh.from);
    const year = date.getFullYear();
    const monthIndex = date.getMonth();
    const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

    const baselineCost =
      bh.importKWh *
        importPricePerKWh(
          bh,
          scenario.tax,
          scenario.supplier,
          scenario.grid,
          baselineAnnualImport,
        ) -
      bh.exportKWh *
        exportRevenuePerKWh(bh, scenario.tax, scenario.supplier, scenario.grid);

    const batteryCost =
      rh.importKWh *
        importPricePerKWh(
          bh,
          scenario.tax,
          scenario.supplier,
          scenario.grid,
          batteryAnnualImport,
        ) -
      rh.exportKWh *
        exportRevenuePerKWh(bh, scenario.tax, scenario.supplier, scenario.grid);

    const saving = baselineCost - batteryCost;
    const existing = byMonth.get(monthKey);
    if (existing) existing.saving += saving;
    else byMonth.set(monthKey, { monthIndex, year, saving });
  }

  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthKey, val]) => ({
      monthKey,
      monthIndex: val.monthIndex,
      year: val.year,
      savingsSek: val.saving,
    }));
}
